import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import notificationService from '../../services/notificationService';
import NotificationDropdown from './NotificationDropdown';
import './NotificationBell.css';

const NotificationBell = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Fetch unread count
  const { data: unreadCount = 0, refetch: refetchUnreadCount } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => notificationService.getUnreadCount(),
    refetchInterval: 30000, // Refetch every 30 seconds
    staleTime: 10000, // Consider data stale after 10 seconds
  });

  // Fetch notifications when dropdown is open
  const { data: notifications = [], isLoading: notificationsLoading, error: notificationsError } = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: () => notificationService.getNotifications(5),
    enabled: isDropdownOpen, // Only fetch when dropdown is open
    staleTime: 5000, // Consider data stale after 5 seconds
    retry: 1, // Only retry once on error
  });

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleNotificationClick = async (notificationId) => {
    try {
      await notificationService.markAsRead(notificationId);
      // Refetch unread count after marking as read
      refetchUnreadCount();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      // Refetch unread count after marking all as read
      refetchUnreadCount();
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDropdownOpen && !event.target.closest('.notification-bell-container')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  return (
    <div className="notification-bell-container">
      <div 
        className="notification-bell" 
        onClick={toggleDropdown}
        title={unreadCount > 0 ? `${unreadCount} unread notifications` : 'No new notifications'}
      >
        <img
          className="headericon"
          src={`${process.env.PUBLIC_URL}/notificationicon.png`}
          alt="Notifications"
        />
        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </div>

      {isDropdownOpen && (
        <NotificationDropdown
          notifications={notifications}
          isLoading={notificationsLoading}
          error={notificationsError}
          onNotificationClick={handleNotificationClick}
          onMarkAllAsRead={handleMarkAllAsRead}
          onClose={() => setIsDropdownOpen(false)}
        />
      )}
    </div>
  );
};

export default NotificationBell;
